import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { concatMap, filter, first, map, Observable, of, race, throwError } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  applyPropsPatch,
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  getNamespaceId,
  getWorkflowInputNameForGeneratedField,
  getWrapperItems,
  WrapperConfirmButton,
  WrapperItem,
  WrapperParameter,
  WrapperRefs,
} from '@blaze-runtime/engine-domain';
import {
  ApiActionParameterInfo,
  ColumnDataType,
  DisplayInfo,
} from '@blaze-shared/api-contracts-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import { ActionConfiguratorFormField, getFieldsConfig } from '@blaze-shared/connectors-domain';
import {
  CanvasElementDataProperties,
  ExpressionObj,
  GENERATED_SOURCE_NODE_NAME,
  getNewApiCallSourceNode,
} from '@blaze-shared/elements-domain';
import { DatasetColumn } from '@blaze-shared/schema-domain';
import { StyleSheet } from '@blaze-shared/theming-domain';
import {
  appendNodeToWorkflowMutably,
  DataAssignment,
  findFirstNodeInWorkflowOfType,
  isApiCallNode,
  isGetNode,
  removeWorkflowNodeMutably,
  Workflow,
} from '@blaze-shared/workflow-domain';

import {
  setApiCallAsDataSource,
  updateElementWrapperFailure,
  updateElementWrapperSuccess,
} from '../+state/elements/elements.actions';
import { ElementsFacade } from '../+state/elements/elements.facade';
import { getActiveInputDataSource } from '../canvas-elements/element-model/data-binding';
import { getFormFieldDataTypeMapping } from '../canvas-elements/element-model/form-fields-generation';
import { getFormInputNameByType } from '../canvas-elements/element-model/io';
import { getWrapperRefs } from '../canvas-elements/element-model/wrapper-refs';
import { FieldInfo } from '../canvas-elements/field-info';
import { mapGenerationToRegularElementType } from '../utils/elements/generation.utils';

import { ElementDefinitionsService } from './element-definitions.service';
import { ElementStylesService, makeStyleConfigPropsForDefaultStyleSheet } from './styles';
import { WorkflowCrudService } from './workflow-crud.service';

export interface SetAsApiDataSourceGenerationData {
  addConfirmButton: boolean;
  connectorId: string;
  actionName: string;
  errorMessage: string;
  fields: ActionConfiguratorFormField[];
  parameters: ApiActionParameterInfo[];
  /** Result columns (provided if schema is known) */
  resultColumns: DatasetColumn[] | undefined;
}

export interface SetAsApiDataSourceResult {
  success: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiCallDataSourceService {
  private readonly actions$ = inject(Actions);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly store = inject(Store);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  /**
   * Sets and updates the data source of an element
   * and regenerates the wrapper.
   *
   * @param elementId the id of the element which should have the API Call data source applied
   * @param generationData additional data provided when the user configures the API Call
   */
  setAsDataSource(
    elementId: CanvasElementId,
    generationData?: SetAsApiDataSourceGenerationData,
  ): Observable<SetAsApiDataSourceResult> {
    return this.getOrCreateInputWorkflow(elementId).pipe(
      concatLatestFrom(() => [this.elementsFacade.entities$, this.elementStylesService.sheets$]),
      concatMap(([inputWorkflow, entities, styleSheets]) => {
        const element = entities[elementId];
        if (!element) {
          throw new Error(`Element ${elementId} could not be found`);
        }
        const refs = getWrapperRefs(element);
        const fields = this.prepareWrapperFields(entities, styleSheets, refs, generationData);

        this.store.dispatch(
          setApiCallAsDataSource({
            elementId,
            fields,
            inputWorkflow: this.prepareWorkflowApiCallNode(inputWorkflow, fields, generationData),
            wrapperDefinition: this.elementDefinitionsService.getDefinitionSafe('wrapper'),
            resultColumns: generationData?.resultColumns,
          }),
        );

        // Wait for result
        return race([
          // Success
          this.actions$.pipe(
            ofType(updateElementWrapperSuccess),
            filter(
              (action) => action.elementId === elementId && action.workflow.id === inputWorkflow.id,
            ),
            map(() => ({ success: true })),
          ),
          // Failure
          this.actions$.pipe(
            ofType(updateElementWrapperFailure),
            filter(
              (action) => action.elementId === elementId && action.workflowId === inputWorkflow.id,
            ),
            concatMap(() =>
              throwError(
                () =>
                  new Error(`Could not save workflow ${inputWorkflow.id} for element ${elementId}`),
              ),
            ),
          ),
        ]).pipe(first());
      }),
    );
  }

  /** Gets the input workflow or generates a new blank one */
  private getOrCreateInputWorkflow(elementId: CanvasElementId): Observable<Workflow> {
    return this.store.select(runtimeSelectors.selectInputWorkflowForElement(elementId)).pipe(
      first(),
      concatLatestFrom(() => this.store.select(fromRouter.getProjectId)),
      concatMap(([workflow, projectId]) =>
        workflow
          ? of(workflow)
          : this.workflowCrudService.createWorkflow({
              inputs: {},
              inputSchemas: [],
              isLongRunning: false,
              isServer: false,
              nodes: [],
              pageId: getNamespaceId(elementId),
              projectId,
              startNodeId: null,
            }),
      ),
    );
  }

  private prepareWorkflowApiCallNode(
    workflow: Workflow,
    fields: FieldInfo[],
    generationData?: SetAsApiDataSourceGenerationData,
  ): Workflow {
    return produce(workflow, (draft) => {
      draft.isDraft = true;

      // Clear non-ApiCall data source node
      const nonApiCallDataSourceNode = draft.nodes.find(
        (node) =>
          (node.id === GENERATED_SOURCE_NODE_NAME && !isApiCallNode(node)) || isGetNode(node),
      );
      removeWorkflowNodeMutably(draft, nonApiCallDataSourceNode);

      let apiCallNode = findFirstNodeInWorkflowOfType(draft, isApiCallNode);
      if (!apiCallNode) {
        apiCallNode = getNewApiCallSourceNode();
        appendNodeToWorkflowMutably(draft, apiCallNode);
      }

      let assignments: DataAssignment[] = apiCallNode.assignments || [];

      if (generationData) {
        apiCallNode.connectorId = generationData.connectorId;
        apiCallNode.actionName = generationData.actionName;
        apiCallNode.errorMessage = generationData.errorMessage;

        // Prepare assignments
        const { defaultAssignments } = getFieldsConfig(generationData.fields);
        assignments = defaultAssignments;
      }

      // If assignment shown on form - use the input for the generated field as value
      for (const assignment of assignments) {
        const matchedField = fields.find(
          (field) =>
            field.type === 'parameter' && (field.fieldId || field.fieldName) === assignment.name,
        );
        if (matchedField) {
          const elementType = matchedField.elementDefinition.type;
          const inputName = getWorkflowInputNameForGeneratedField({
            filterType: 'parameter',
            fieldName: assignment.name,
          });
          assignment.value = inputName ? getInputNameForElementType(inputName, elementType) : '';
          assignment.displayInfos = [];
        }
      }

      apiCallNode.assignments = assignments;
      // Update parameters based on assignments
      apiCallNode.parameters = assignments.reduce((acc, assignment) => {
        acc[assignment.name] = assignment.value;
        return acc;
      }, {} as any);
    });
  }

  private prepareWrapperFields(
    entities: Dictionary<CanvasElement>,
    styleSheets: StyleSheet[],
    refs: WrapperRefs,
    generationData?: SetAsApiDataSourceGenerationData,
  ): FieldInfo[] {
    const wrapperItems = getWrapperItems(refs);

    const fieldInfos: FieldInfo[] = generationData
      ? // Wrapper has been (re)configured in API Call configurator - generate based on configurator field data
        this.prepareParameterFieldInfosFromConfigurator(
          generationData,
          wrapperItems,
          entities,
          styleSheets,
        )
      : // Wrapper is updated - prepare field infos from wrapper refs info
        this.prepareParameterFieldInfosFromRefs(wrapperItems, entities, styleSheets);

    // Add confirm button - use generation data or preserve the state
    const addConfirmButton = generationData
      ? generationData.addConfirmButton
      : wrapperItems.some((item) => item.type === 'confirm');

    // If Confirm button should be added (validate that there are User Input/Show on Form fields)
    if (addConfirmButton === true && fieldInfos.length > 0) {
      fieldInfos.push(this.prepareConfirmButton(styleSheets));
    }

    return fieldInfos;
  }

  private prepareParameterFieldInfosFromConfigurator(
    generationData: SetAsApiDataSourceGenerationData,
    wrapperItems: WrapperItem[],
    entities: Dictionary<CanvasElement>,
    styleSheets: StyleSheet[],
  ): FieldInfo[] {
    return generationData.fields
      .filter((field) => field.showOnForm)
      .map((field) => {
        let elementType: CanvasElementType;
        let parameterType: ColumnDataType | undefined = undefined;

        // If element already exists - use its element type
        const matchedElementId = wrapperItems.find(
          (item) => item.type === 'parameter' && item.value === field.name,
        )?.elementId;
        const matchedElement = matchedElementId && entities[matchedElementId];
        if (matchedElement) {
          elementType = matchedElement.type;
        } else {
          const parameter = generationData.parameters.find(
            (parameter) => parameter.name === field.name,
          );
          parameterType = parameter?.userDataType ?? parameter?.dataType ?? undefined;
          if (!parameterType) {
            throw new Error(`Field name (${field.name}) is missing in the parameter list`);
          }

          const fieldDataTypeMapping = getFormFieldDataTypeMapping(false, parameterType);
          elementType = mapGenerationToRegularElementType(fieldDataTypeMapping?.[0].value);
        }

        return this.prepareParameterField(
          field.name,
          field.value,
          field.displayInfos,
          elementType,
          styleSheets,
          parameterType,
        );
      });
  }

  private prepareParameterFieldInfosFromRefs(
    wrapperItems: WrapperItem[],
    entities: Dictionary<CanvasElement>,
    styleSheets: StyleSheet[],
  ): FieldInfo[] {
    return wrapperItems
      .filter((item) => item.type === 'parameter')
      .map((item) => {
        const element = entities[item.elementId];
        if (!element || !item.value) {
          return;
        }

        // Populate expression and display infos from existing element
        const inputDataSource = getActiveInputDataSource(element);

        if (inputDataSource?.expression && typeof inputDataSource.expression !== 'string') {
          return;
        }

        return this.prepareParameterField(
          item.value,
          inputDataSource?.expression || '',
          inputDataSource?.displayInfos || [],
          element.type,
          styleSheets,
        );
      })
      .filter((item): item is FieldInfo => !!item);
  }

  /**
   * Prepare parameter field
   * @param fieldName
   * @param fieldValue
   * @param fieldDisplayInfos
   * @param parameterType if generated from API
   * @param elementType
   * @returns
   */
  private prepareParameterField(
    fieldName: string,
    fieldValue: string | ExpressionObj[],
    fieldDisplayInfos: DisplayInfo[],
    elementType: CanvasElementType,
    styleSheets: StyleSheet[],
    fieldType?: ColumnDataType,
  ): FieldInfo {
    const elementDefinition = this.elementDefinitionsService.getDefinitionSafe(elementType);

    // Prepare the initial values
    const baseProps = applyPropsPatch(
      elementDefinition.initial,
      makeStyleConfigPropsForDefaultStyleSheet(styleSheets, elementDefinition.type),
    );

    const propsBuilder = this.elementDefinitionsService.getPropsBuilder(elementType, baseProps);

    const baseFilterFieldProps = propsBuilder
      ?.setPlaceholder(fieldName)
      .setMargin('$t.spacingSearchAndFilters')
      .build();

    const generateFilterField =
      this.elementDefinitionsService.getDefinition(elementType)?.generation?.filterField;

    const initProps =
      generateFilterField?.({
        fieldName,
        fieldType,
        props: baseFilterFieldProps,
      }) ?? baseFilterFieldProps;

    // Apply the default value
    const defaultValueBinding = prepareDefaultValueBinding(
      elementType,
      fieldValue,
      fieldDisplayInfos,
    );
    const updateProps = this.elementDefinitionsService
      .getPropsBuilder(elementType)
      ?.setBinding(defaultValueBinding, true)
      .build();

    const result: FieldInfo = {
      refIds: WrapperParameter.getRefIds(fieldName),
      type: 'parameter',
      fieldName,
      elementDefinition,
      initProps,
      updateProps,
    };
    return result;
  }

  private prepareConfirmButton(styleSheets: StyleSheet[]): FieldInfo<'button'> {
    const elementDefinition = this.elementDefinitionsService.getDefinitionSafe('button');
    const initProps = this.elementDefinitionsService
      .getPropsBuilder('button', {
        content: {
          button: {
            label: 'Apply',
          },
        },
        ...makeStyleConfigPropsForDefaultStyleSheet(styleSheets, elementDefinition.type),
      })
      ?.setMargin('$t.spacingSearchAndFilters')
      .build();

    return {
      refIds: WrapperConfirmButton.getRefIds(),
      type: 'confirm',
      elementDefinition,
      initProps,
    };
  }
}

function prepareDefaultValueBinding(
  elementType: CanvasElementType,
  value: string | ExpressionObj[],
  displayInfos: DisplayInfo[],
): CanvasElementDataProperties {
  const binding: CanvasElementDataProperties = {
    key: getFormInputNameByType(elementType),
    bindings: {},
    dataSource: {
      sourceType: 'expression',
      id: '',
      columns: [],
      expression: value,
      displayInfos,
    },
    enabled: true,
  };

  // If no value provided, clean up the expression binding
  if (!value) {
    binding.deleted = true;
  }

  return binding;
}

function getInputNameForElementType(inputName: string, elementType: CanvasElementType): string {
  const path = [`$$${inputName}`];

  let suffix: string | undefined;

  switch (elementType) {
    case 'dropdown':
    case 'radio':
    case 'tags':
      suffix = 'value';
      break;
    case 'audioRecorder':
    case 'uploadFile':
      suffix = 'url';
      break;
    default:
      break;
  }

  if (suffix) {
    path.push(suffix);
  }

  return path.join('.');
}
